import React from 'react';

/**
 * Shown below an assistant message when the beneficiary update handler has
 * collected all required params and wants the participant to verify the
 * captured name / relationship / allocation before submitting to the queue.
 *
 * On "Confirm", auto-sends a specific phrase that the backend recognises as
 * "proceed with beneficiary designation" to skip the review step and execute.
 */
export default function BeneficiaryReviewCard({ params, onConfirm, onCancel, isResolving }) {
  const primary    = params?.primary_beneficiaries    || [];
  const contingent = params?.contingent_beneficiaries || [];

  return (
    <div className="bg-bg-surface border border-accent/40 rounded-lg p-4 max-w-md">
      <h3 className="text-sm font-semibold text-text mb-2 flex items-center gap-2">
        <span className="text-accent">👥</span> Review Beneficiary Designation
      </h3>
      <p className="text-[13px] text-text-muted mb-3 leading-relaxed">
        Please confirm the details below before submitting for plan administrator review.
      </p>

      {primary.length > 0 && (
        <div className="mb-2">
          <p className="text-[11px] font-mono uppercase tracking-widest text-text-faint mb-1.5">Primary</p>
          <ul className="space-y-1">
            {primary.map((b, i) => (
              <li key={i} className="flex items-center justify-between text-[13px] bg-bg-s2 rounded px-3 py-1.5">
                <span className="font-medium text-text">{b.name}</span>
                <span className="text-text-muted capitalize">{b.relationship} · {b.allocation_pct ?? 100}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {contingent.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-mono uppercase tracking-widest text-text-faint mb-1.5">Contingent</p>
          <ul className="space-y-1">
            {contingent.map((b, i) => (
              <li key={i} className="flex items-center justify-between text-[13px] bg-bg-s2 rounded px-3 py-1.5">
                <span className="font-medium text-text">{b.name}</span>
                <span className="text-text-muted capitalize">{b.relationship} · {b.allocation_pct ?? 100}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-[12px] text-text-faint mb-3">
        If the name or relationship looks wrong, click Cancel and describe the correction.
      </p>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={isResolving}
          className="px-4 py-2 rounded-md text-[13px] font-medium bg-accent hover:bg-accent-dark text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isResolving ? 'Submitting…' : 'Confirm & Submit'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isResolving}
          className="px-4 py-2 rounded-md text-[13px] font-medium bg-bg-s2 border border-border-strong text-text-muted hover:text-text transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
